import { locale } from '../lib/locale';

/**
 * Freizeit (1.19; the shopping list moved to kitchen.ts in 1.20). Glossary: Freizeit = Fun (“Family fun”), Ausflugsidee =
 * day-out idea, Gutschein = voucher, Wunschzettel = wish list. German is the master.
 */
export const FUN_TEXT = {
  de: {
    family: 'Familie',
    intro: 'Was wir vorhaben, was in der Schublade liegt und was wir uns wünschen.',
    goodToKnow: 'Gut zu wissen',
    ideas: 'Ausflugsideen',
    allIdeas: (n: string) => `Alle ${n} →`,
    idea: 'Idee',
    hearts: ' Herzen',
    ideasEmpty:
      'Wildpark, Kletterpark, Eis am See? Sammelt hier, was ihr mal machen wollt – jeder kann eine Idee eintragen.',
    vouchers: 'Gutscheine',
    openVouchers: (n: string) => `${n} offen →`,
    voucher: 'Gutschein',
    noExpiry: 'ohne Ablauf',
    vouchersEmpty:
      'Liegen bei euch auch Gutscheine in der Schublade? Tragt sie hier ein – die App denkt rechtzeitig dran, bevor sie ablaufen.',
    wishLists: 'Wunschzettel',
    wishes: (n: number) => (n === 1 ? '1 Wunsch' : `${n} Wünsche`),
    claimsHint: 'Was jemand besorgt, sehen nur Erwachsene – nie auf dem eigenen Zettel.',
    newVoucher: 'Neuer Gutschein',
    newIdea: 'Neue Idee',
    // ideas page
    wantToDo: 'Wollen wir mal machen',
    show: 'Zeigen',
    all: 'Alle',
    outdoors: 'Draußen',
    rainy: 'Bei Regen',
    noMatch: 'Dazu gibt es gerade keine Idee.',
    noIdeas:
      'Noch keine Ideen. Was wolltet ihr schon immer mal machen? Jeder kann eine eintragen – und mit einem Herz zeigen: da will ich hin.',
    planned: 'Eingeplant',
    done: 'Erlebt',
    onDay: (date: string) => `am ${date}`,
    backOnList: 'Wieder auf die Liste',
    editIdea: 'Idee ändern',
    deleteIdea: 'Idee löschen',
    plan: 'Einplanen',
    editItem: (title: string) => `${title} ändern`,
    ideaBy: (name: string) => `Idee von ${name}`,
    wantGo: (names: string, n: number) => `${names} ${n === 1 ? 'will' : 'wollen'} hin`,
    iWant: 'Ich will hin',
    meToo: 'Ich auch',
    pickName: 'Für ein Herz oben deinen Namen wählen.',
    // vouchers page
    photoVoucher: 'Gutschein abfotografieren',
    voucherPhoto: 'Foto vom Gutschein',
    type: 'Tippen',
    addVoucher: 'Gutschein eintragen',
    vouchersCount: (n: number) => (n === 1 ? '1 Gutschein' : `${n} Gutscheine`),
    firstOnTop: 'Der, der zuerst abläuft, steht oben.',
    noOpenPlan:
      'Keine offenen Gutscheine. Liegt einer in der Schublade? Abfotografieren oder eintippen – die App erinnert rechtzeitig, bevor er abläuft.',
    noOpen: 'Gerade keine offenen Gutscheine.',
    past: (n: string) => `Eingelöst und abgelaufen (${n})`,
    redeemed: 'eingelöst',
    expired: 'abgelaufen',
    notRedeemed: 'Doch nicht eingelöst',
    deleteItem: (title: string) => `${title} löschen`,
    editVoucher: 'Gutschein ändern',
    deleteVoucher: 'Gutschein löschen',
    voucherBring: 'Gutschein',
    keptIn: (place: string) => `Gutschein liegt in: ${place}`,
    codeIs: (code: string) => `Code: ${code}`,
    noEnd: 'läuft nicht ab',
    forAll: 'für alle',
    forNames: (names: string) => `für ${names}`,
    from: (name: string) => `von ${name}`,
    liesIn: 'Liegt in:',
    code: 'Code',
    used: 'Eingelöst',
    stopReminding: 'Nicht mehr erinnern',
    // wishes page
    listTitle: (name: string) => (/[sßxz]$/i.test(name) ? `${name}' Wünsche` : `${name}s Wünsche`),
    myWishes: 'Meine Wünsche',
    familyWishes: 'Familienwünsche',
    wish: 'Wunsch',
    forWhat: 'Wofür',
    birthday: 'Geburtstag',
    christmas: 'Weihnachten',
    familyEmpty: 'Was wünscht ihr euch als Familie? Trampolin, ein neues Spiel, ein Ausflug …',
    ownEmpty: 'Dein Wunschzettel ist noch leer. Was wünschst du dir?',
    otherEmpty: (name: string) => `${name} hat sich noch nichts gewünscht.`,
    surprise: 'Was jemand besorgt, steht hier nicht – damit es eine Überraschung bleibt.',
    got: 'Bekommen',
    backOnWishes: (title: string) => `${title} wieder auf den Wunschzettel`,
    editWish: 'Wunsch ändern',
    newWish: 'Neuer Wunsch',
    deleteWish: 'Wunsch löschen',
    loveOf: (n: string) => `${n} von 3 Herzen`,
    heartWish: ' Herzenswunsch',
    link: 'Link',
    gets: (names: string, n: number) => `${names} ${n > 1 ? 'besorgen' : 'besorgt'} das`,
    and: ' und ',
    notAfterAll: 'Doch nicht',
    iGetIt: 'Ich besorge das',
    whoGets: 'Wer besorgt das?',
    together: 'Zusammen schenken',
    whoGetsThis: (title: string) => `Wer besorgt „${title}“?`,
    whoGives: 'Wer schenkt?',
    adultsOnly: (name: string) => `Das sehen nur Erwachsene – ${name} nicht.`,
    theKids: 'die Kinder',
    remember: 'Merken',
    // forms
    places: {
      out: { label: 'Draußen', description: 'braucht trockenes Wetter' },
      in: { label: 'Drinnen', description: 'auch bei Regen' },
      any: { label: 'Egal', description: 'geht immer' },
    },
    whereWhat: 'Wohin oder was?',
    ideaExample: 'z. B. Wildpark, Kletterpark, Eis essen am See',
    free: 'kostenlos',
    where: 'Wo?',
    howLong: 'Wie lange?',
    dontKnow: 'Weiß nicht',
    moreIdea: 'Kosten, Notiz',
    costs: 'Kosten',
    costsExample: 'z. B. kostenlos, ca. 60 €',
    note: 'Notiz',
    noteExample: 'z. B. ab 8 Jahren, Picknick mitnehmen',
    saveIdea: 'Idee merken',
    save: 'Speichern',
    readVoucher: 'Das ist ein Gutschein. Lies ihn aus.',
    takePhoto: 'Foto machen',
    pickImage: 'Bild wählen',
    reading: 'Die KI liest den Gutschein …',
    readDone: 'Gelesen – bitte kurz prüfen und eintragen, wo er liegt.',
    readNothing: 'Auf dem Foto habe ich keinen Gutschein erkannt. Einfach selbst eintragen.',
    readHint: 'Abfotografieren – die KI liest Wert, „gültig bis“ und Code.',
    aiHint: 'Mit dem KI-Helfer liest die App Gutscheine vom Foto.',
    settings: 'Einstellungen',
    whatWhere: 'Was und wo?',
    voucherExample: 'z. B. Therme am Stadtpark, Kino im Zentrum',
    value: 'Wert',
    valueExample: 'z. B. 25 €, 2 Personen',
    validUntil: 'Gültig bis',
    validHint: 'Leer lassen, wenn er nicht abläuft.',
    liesInLabel: 'Liegt in',
    placeExample: 'z. B. Schublade Flur',
    placeHint: 'Damit ihr ihn findet, wenn es so weit ist.',
    forWhom: 'Für wen?',
    moreVoucher: 'Code, von wem, Notiz',
    fromWhom: 'Von wem?',
    fromExample: 'z. B. Oma',
    love: [
      { value: '1', label: '♥', description: 'wär schön' },
      { value: '2', label: '♥♥', description: 'sehr gern' },
      { value: '3', label: '♥♥♥', description: 'Herzenswunsch' },
    ],
    familyWishAsk: 'Was wünscht ihr euch zusammen?',
    wishAsk: 'Was wünschst du dir?',
    familyWishExample: 'z. B. Trampolin, Gesellschaftsspiel',
    wishExample: 'z. B. Fahrrad, Buch, Hörspiel',
    howMuch: 'Wie sehr?',
    forWhatAsk: 'Wofür?',
    morePrice: 'Preis, Link',
    price: 'Preis',
    priceExample: 'z. B. ca. 60 €',
    linkExample: 'z. B. eine Seite im Internet',
    badLink: 'Das ist keine Internetadresse.',
    toWishList: 'Auf den Wunschzettel',
    // places on the map
    route: 'Route',
    website: 'Webseite',
    call: 'Anrufen',
    removePlace: 'Ort entfernen',
    nothingOnMap: 'Auf der Karte nichts gefunden – passt auch so.',
    foundOnMap: 'Auf der Karte gefunden',
    foundHint: 'Auf der Karte gefunden – antippen übernimmt Adresse, Entfernung und mehr:',
    loading: 'Lädt …',
    osm: 'Kartendaten © OpenStreetMap-Mitwirkende',
    kinds: {
      zoo: 'Zoo / Wildpark',
      theme_park: 'Freizeitpark',
      water_park: 'Spaßbad',
      swimming_pool: 'Schwimmbad',
      museum: 'Museum',
      park: 'Park',
      playground: 'Spielplatz',
      attraction: 'Sehenswürdigkeit',
      viewpoint: 'Aussichtspunkt',
      nature_reserve: 'Naturschutzgebiet',
      cinema: 'Kino',
      theatre: 'Theater',
      restaurant: 'Restaurant',
      cafe: 'Café',
      ice_cream: 'Eisdiele',
      aquarium: 'Aquarium',
      castle: 'Burg / Schloss',
      miniature_golf: 'Minigolf',
      sports_centre: 'Sportzentrum',
      bowling_alley: 'Bowling',
      ice_rink: 'Eisbahn',
      peak: 'Berg',
      beach: 'Strand',
      lake: 'See',
      water: 'See',
      city: 'Stadt',
      town: 'Stadt',
      village: 'Ort',
    } as Record<string, string>,
  },
  en: {
    family: 'Family',
    intro: 'What we’re planning, what’s in the drawer and what we’re wishing for.',
    goodToKnow: 'Good to know',
    ideas: 'Day-out ideas',
    allIdeas: (n: string) => `All ${n} →`,
    idea: 'Idea',
    hearts: ' hearts',
    ideasEmpty:
      'Wildlife park, climbing park, ice cream by the lake? Collect what you’d like to do – anyone can add an idea.',
    vouchers: 'Vouchers',
    openVouchers: (n: string) => `${n} open →`,
    voucher: 'Voucher',
    noExpiry: 'no expiry',
    vouchersEmpty:
      'Got vouchers in a drawer too? Add them here – the app reminds you in time before they run out.',
    wishLists: 'Wish lists',
    wishes: (n: number) => (n === 1 ? '1 wish' : `${n} wishes`),
    claimsHint: 'Only adults see who’s getting what – never on their own list.',
    newVoucher: 'New voucher',
    newIdea: 'New idea',
    wantToDo: 'Things we’d like to do',
    show: 'Show',
    all: 'All',
    outdoors: 'Outdoors',
    rainy: 'When it rains',
    noMatch: 'No idea for that right now.',
    noIdeas:
      'No ideas yet. What have you always wanted to do? Anyone can add one – and show with a heart: I want to go there.',
    planned: 'Planned',
    done: 'Done it',
    onDay: (date: string) => `on ${date}`,
    backOnList: 'Back on the list',
    editIdea: 'Edit idea',
    deleteIdea: 'Delete idea',
    plan: 'Plan it',
    editItem: (title: string) => `Edit ${title}`,
    ideaBy: (name: string) => `Idea from ${name}`,
    wantGo: (names: string, n: number) => `${names} ${n === 1 ? 'wants' : 'want'} to go`,
    iWant: 'I want to go',
    meToo: 'Me too',
    pickName: 'Pick your name at the top to give a heart.',
    photoVoucher: 'Take a photo of a voucher',
    voucherPhoto: 'Photo of the voucher',
    type: 'Type it',
    addVoucher: 'Add voucher',
    vouchersCount: (n: number) => (n === 1 ? '1 voucher' : `${n} vouchers`),
    firstOnTop: 'The one that runs out first is at the top.',
    noOpenPlan:
      'No open vouchers. Is there one in the drawer? Take a photo or type it in – the app reminds you in time before it runs out.',
    noOpen: 'No open vouchers right now.',
    past: (n: string) => `Redeemed and expired (${n})`,
    redeemed: 'redeemed',
    expired: 'expired',
    notRedeemed: 'Not redeemed after all',
    deleteItem: (title: string) => `Delete ${title}`,
    editVoucher: 'Edit voucher',
    deleteVoucher: 'Delete voucher',
    voucherBring: 'Voucher',
    keptIn: (place: string) => `Voucher kept in: ${place}`,
    codeIs: (code: string) => `Code: ${code}`,
    noEnd: 'doesn’t expire',
    forAll: 'for everyone',
    forNames: (names: string) => `for ${names}`,
    from: (name: string) => `from ${name}`,
    liesIn: 'Kept in:',
    code: 'Code',
    used: 'Redeemed',
    stopReminding: 'Stop reminding me',
    listTitle: (name: string) => `${name}’s wishes`,
    myWishes: 'My wishes',
    familyWishes: 'Family wishes',
    wish: 'Wish',
    forWhat: 'For',
    birthday: 'Birthday',
    christmas: 'Christmas',
    familyEmpty: 'What would you like as a family? A trampoline, a new game, a trip …',
    ownEmpty: 'Your wish list is still empty. What would you like?',
    otherEmpty: (name: string) => `${name} hasn’t wished for anything yet.`,
    surprise: 'Who’s getting what isn’t shown here – so it stays a surprise.',
    got: 'Got it',
    backOnWishes: (title: string) => `Put ${title} back on the wish list`,
    editWish: 'Edit wish',
    newWish: 'New wish',
    deleteWish: 'Delete wish',
    loveOf: (n: string) => `${n} of 3 hearts`,
    heartWish: ' Dearest wish',
    link: 'Link',
    gets: (names: string, n: number) => `${names} ${n > 1 ? 'are getting' : 'is getting'} it`,
    and: ' and ',
    notAfterAll: 'Not after all',
    iGetIt: 'I’ll get it',
    whoGets: 'Who’s getting it?',
    together: 'Give it together',
    whoGetsThis: (title: string) => `Who’s getting “${title}”?`,
    whoGives: 'Who’s giving it?',
    adultsOnly: (name: string) => `Only adults see this – not ${name}.`,
    theKids: 'the kids',
    remember: 'Save',
    places: {
      out: { label: 'Outdoors', description: 'needs dry weather' },
      in: { label: 'Indoors', description: 'fine in the rain' },
      any: { label: 'Either', description: 'always works' },
    },
    whereWhat: 'Where to, or what?',
    ideaExample: 'e.g. wildlife park, climbing park, ice cream by the lake',
    free: 'free',
    where: 'Where?',
    howLong: 'How long?',
    dontKnow: 'Not sure',
    moreIdea: 'Costs, note',
    costs: 'Costs',
    costsExample: 'e.g. free, about €60',
    note: 'Note',
    noteExample: 'e.g. 8 and over, bring a picnic',
    saveIdea: 'Save idea',
    save: 'Save',
    readVoucher: 'This is a voucher. Read it.',
    takePhoto: 'Take a photo',
    pickImage: 'Choose a picture',
    reading: 'The AI is reading the voucher …',
    readDone: 'Read – please check and add where it’s kept.',
    readNothing: 'I couldn’t find a voucher on the photo. Just add it yourself.',
    readHint: 'Take a photo – the AI reads the value, “valid until” and the code.',
    aiHint: 'With the AI helper, the app reads vouchers from a photo.',
    settings: 'Settings',
    whatWhere: 'What and where?',
    voucherExample: 'e.g. spa at the park, cinema in town',
    value: 'Value',
    valueExample: 'e.g. €25, 2 people',
    validUntil: 'Valid until',
    validHint: 'Leave empty if it doesn’t expire.',
    liesInLabel: 'Kept in',
    placeExample: 'e.g. hallway drawer',
    placeHint: 'So you find it when the time comes.',
    forWhom: 'For whom?',
    moreVoucher: 'Code, from whom, note',
    fromWhom: 'From whom?',
    fromExample: 'e.g. Grandma',
    love: [
      { value: '1', label: '♥', description: 'would be nice' },
      { value: '2', label: '♥♥', description: 'would love it' },
      { value: '3', label: '♥♥♥', description: 'dearest wish' },
    ],
    familyWishAsk: 'What would you like together?',
    wishAsk: 'What would you like?',
    familyWishExample: 'e.g. trampoline, board game',
    wishExample: 'e.g. bike, book, audiobook',
    howMuch: 'How much?',
    forWhatAsk: 'For what?',
    morePrice: 'Price, link',
    price: 'Price',
    priceExample: 'e.g. about €60',
    linkExample: 'e.g. a web page',
    badLink: 'That isn’t a web address.',
    toWishList: 'Add to the wish list',
    route: 'Route',
    website: 'Website',
    call: 'Call',
    removePlace: 'Remove place',
    nothingOnMap: 'Nothing found on the map – that’s fine too.',
    foundOnMap: 'Found on the map',
    foundHint: 'Found on the map – tap to take over the address, distance and more:',
    loading: 'Loading …',
    osm: 'Map data © OpenStreetMap contributors',
    kinds: {
      zoo: 'Zoo / wildlife park',
      theme_park: 'Theme park',
      water_park: 'Water park',
      swimming_pool: 'Swimming pool',
      museum: 'Museum',
      park: 'Park',
      playground: 'Playground',
      attraction: 'Attraction',
      viewpoint: 'Viewpoint',
      nature_reserve: 'Nature reserve',
      cinema: 'Cinema',
      theatre: 'Theatre',
      restaurant: 'Restaurant',
      cafe: 'Café',
      ice_cream: 'Ice cream parlour',
      aquarium: 'Aquarium',
      castle: 'Castle',
      miniature_golf: 'Crazy golf',
      sports_centre: 'Sports centre',
      bowling_alley: 'Bowling',
      ice_rink: 'Ice rink',
      peak: 'Mountain',
      beach: 'Beach',
      lake: 'Lake',
      water: 'Lake',
      city: 'City',
      town: 'Town',
      village: 'Village',
    } as Record<string, string>,
  },
} as const;

export const funText = FUN_TEXT[locale];
